/**
 * Lightweight in-house i18n for the Veraso app.
 *
 * Why not react-i18next? The app has ~50–100 strings that matter for
 * Phase 1. A small dict + a hook is plenty, has zero deps, and stays
 * easy to read for future contributors.
 *
 * Adding a translation:
 *   1. Add the key to `DICT.fi` and `DICT.en` below.
 *   2. Call `t("your.key")` from any component via `useT()`.
 *
 * Missing key? The translator falls back gracefully:
 *   - Tries the other language's value
 *   - Otherwise returns the key itself (visible debugging signal)
 */
import { useAuth } from "@/contexts/AuthContext";

const DICT = {
  fi: {
    /* nav / appshell */
    "nav.menu": "Valikko",
    "nav.home": "Etusivu",
    "nav.newReport": "Uusi raportti",
    "nav.newQuick": "Uusi pikaraportti",
    "nav.newNotice": "Uusi asukastiedote",
    "nav.properties": "Kohteet",
    "nav.templates": "Työpohjat",
    "nav.settings": "Asetukset",
    "nav.logout": "Kirjaudu ulos",
    "nav.user": "Käyttäjä",
    "nav.asetukset": "Asetukset",
    "nav.kirjauduUlos": "Kirjaudu ulos",

    /* dashboard */
    "dash.eyebrow": "Hallintapaneeli",
    "dash.title": "Tervetuloa",
    "dash.subtitle": "Raportit ja asukastiedotteet samassa paikassa.",
    "dash.subtitleNoNotices": "Kaikki raporttisi yhdessä paikassa.",
    "dash.createNotice": "Luo asukastiedote",
    "dash.createQuick": "Luo pikaraportti",
    "dash.createReport": "Uusi raportti",
    "dash.searchPlaceholder": "Hae nimellä, asiakkaalla, osoitteella…",
    "dash.allStatuses": "Kaikki tilat",
    "dash.clear": "Tyhjennä",
    "dash.tabReports": "Raportit",
    "dash.tabRecentReports": "Viimeisimmät raportit",
    "dash.tabQuick": "Pikaraportit",
    "dash.tabRecentQuick": "Viimeisimmät pikaraportit",
    "dash.tabNotices": "Asukastiedotteet",
    "dash.tabRecentNotices": "Viimeisimmät asukastiedotteet",
    "dash.loading": "Ladataan…",
    "dash.noMatches": "Ei osumia",
    "dash.tryDifferent": "Kokeile muuttaa hakuehtoja.",
    "dash.emptyReportsUnfiled": "Ei hallinnoimattomia raportteja",
    "dash.emptyReports": "Ei vielä raportteja",
    "dash.emptyReportsSub": "Luo ensimmäinen raporttisi muutamalla klikkauksella.",
    "dash.emptyCreateReport": "Luo raportti",
    "dash.emptyQuick": "Ei vielä pikaraportteja",
    "dash.emptyQuickSub": "Pikaraportti on nopea tapa kirjata työ pelkillä kuvilla ja tekstillä – ilman vaiheita ja yksiköitä.",
    "dash.emptyNotices": "Ei vielä tiedotteita",
    "dash.emptyNoticesSub": "Luo siisti asukastiedote muutamassa minuutissa – valitse pohja, täytä tiedot, tulosta tai jaa.",
    "dash.unitOne": "yksikkö",
    "dash.unitMany": "yksikköä",
    "dash.photosCount": "kuvaa",
    "dash.noPhotos": "Ei kuvia",
    "dash.toOffice": "Toimistolle",
    "dash.toOfficeTitle": "Tämä raportti sisältää havainnon, joka on merkitty toimistolle",
    "dash.moveToProperty": "Siirrä kohteeseen",
    "dash.deleteTitle": "Poista",
    "dash.crumbAll": "Kaikki",
    "dash.crumbUnfiled": "Hallinnoimaton",
    "dash.seeAll": "Näytä kaikki",
    "dash.reportsLower": "raporttia kohteissa",

    /* status badges */
    "status.draft": "Luonnos",
    "status.in_progress": "Kesken",
    "status.completed": "Valmis",
    "status.sent": "Lähetetty",

    /* report wizard */
    "wiz.back": "Takaisin",
    "wiz.eyebrow": "Uusi raportti",
    "wiz.heading": "Luo raportti muutamassa vaiheessa.",
    "wiz.step": "Vaihe",
    "wiz.steps.work": "Työ",
    "wiz.steps.units": "Yksiköt",
    "wiz.steps.save": "Tallennus",
    "wiz.steps.summary": "Yhteenveto",
    /* unit kinds (backend palauttaa FI-labelit, ylikirjoitetaan frontilla) */
    "wiz.kind.huoneistot": "Huoneistot",
    "wiz.kind.huoneistot.tpl": "Asunto",
    "wiz.kind.laitteet": "Laitteet",
    "wiz.kind.laitteet.tpl": "Laite",
    "wiz.kind.tarkastuspisteet": "Tarkastuspisteet",
    "wiz.kind.tarkastuspisteet.tpl": "Piste",
    "wiz.kind.tilat": "Tilat",
    "wiz.kind.tilat.tpl": "Tila",
    "wiz.kind.ajoneuvot": "Ajoneuvot",
    "wiz.kind.ajoneuvot.tpl": "Ajoneuvo",
    "wiz.kind.rakennukset": "Rakennukset",
    "wiz.kind.rakennukset.tpl": "Rakennus",
    "wiz.kind.mukautettu": "Mukautettu / Työvaiheet",
    "wiz.kind.default.tpl": "Kohde",
    "wiz.customPlaceholder": "A1\nA2\nA3\nLämmönjakohuone\nPiippu 1\nPiippu 2\nSaunaosasto",

    /* property picker */
    "pp.label": "Kohde (valinnainen)",
    "pp.none": "Ei kohdetta",
    "pp.loading": "Ladataan…",
    "pp.noGroupsYet": "Ei vielä pääryhmiä. Luo ensimmäinen alla.",
    "pp.noProps": "Ei kohteita",
    "pp.selected": "Valittu",
    "pp.createNew": "Luo uusi kohde",
    "pp.propNamePh": "Kohteen nimi (esim. As Oy Kirjokuja 4)",
    "pp.group": "Pääryhmä",
    "pp.pickGroup": "Valitse pääryhmä…",
    "pp.newGroup": "+ Luo uusi pääryhmä",
    "pp.newGroupPh": "Uuden pääryhmän nimi (esim. Taloyhtiöt)",
    "pp.cancel": "Peruuta",
    "pp.creating": "Luodaan…",
    "pp.create": "Luo kohde",
    "pp.nameRequired": "Anna kohteen nimi",
    "pp.groupNameRequired": "Anna pääryhmän nimi",
    "pp.createdToast": "Kohde luotu",
    "pp.createFailed": "Kohteen luonti epäonnistui",
    "pp.unfiledGroup": "Hallinnoimaton",
    "wiz.previous": "Edellinen",
    "wiz.next": "Seuraava",
    "wiz.creating": "Luodaan…",
    "wiz.create": "Luo raportti",
    "wiz.s0.title": "Mikä työ on tehtävä?",
    "wiz.s0.sub": "Kirjoita työn nimi vapaasti.",
    "wiz.s0.workName": "Työn nimi",
    "wiz.s0.workPlaceholder": "esim. Hajulukon vaihto, Suodatinvaihto, Sähkötarkastus, Kattohuolto…",
    "wiz.s0.propType": "Kohdetyyppi",
    "wiz.s0.propTypeOptional": "(valinnainen)",
    "wiz.s0.propTypePlaceholder": "esim. kerrostalo, halli, varasto, toimisto…",
    "wiz.s1.title": "Mitä yksiköitä tai työvaiheita raportissa käsitellään?",
    "wiz.s1.sub": "Valitse yksiköt, tilat, laitteet tai työvaiheet joita raportissa käsitellään.",
    "wiz.s1.pickKind": "Valitse yksikkötyyppi",
    "wiz.s1.labelPrefix": "Yksikön nimi (etuliite)",
    "wiz.s1.count": "Määrä",
    "wiz.s1.customLabel": "Yksikkönimet (yksi per rivi)",
    "wiz.s1.customHint": "voit lisätä lisää myös myöhemmin kentällä.",
    "wiz.s1.preview": "Esikatselu",
    "wiz.s1.moreLabel": "muuta",
    "wiz.s2.title": "Kohde ja raportin sisältö",
    "wiz.s2.sub": "Asiakkaan tiedot täydennetään myöhemmin raporttinäkymässä. Voit jättää muutkin kentät tyhjäksi ja palata niihin kentällä.",
    "wiz.s2.executor": "Suorittajayritys",
    "wiz.s2.executorPlaceholder": "Oma yritys (näkyy raportissa)",
    "wiz.s2.askExecutor": "Kysy työn suorittajaa",
    "wiz.s2.askExecutorHint": "Poista valinta, jos et halua näyttää erillistä suorittaja-kenttää raportilla.",
    "wiz.s2.property": "Kohde (valinnainen)",
    "wiz.s2.checklist": "Checklist / työvaiheet",
    "wiz.s2.checklistHint": "Valitse yrityksen vakio-checklist tai lisää omia työvaiheita raporttikohtaisesti. Työvaiheet kopioituvat kaikille yksiköille.",
    "wiz.s2.companyChecklists": "Yrityksen checklistit",
    "wiz.s2.noChecklistsYet": "Ei vielä tallennettuja checklistejä. Voit lisätä niitä",
    "wiz.s2.inSettings": "Asetuksissa",
    "wiz.s2.addStep": "Lisää raporttikohtainen työvaihe…",
    "wiz.s2.materials": "Tarvikkeet – pikavalinnat",
    "wiz.s2.materialsHint": "Nämä ovat tarvikkeita jotka ovat saatavilla tässä raportissa – ei käytettyjä tarvikkeita. Kenttäkäyttäjä lisää kussakin yksikössä mitä tarvikkeita siihen tosiasiassa käytettiin.",
    "wiz.s2.companyMaterials": "Yrityksen vakiotarvikkeet",
    "wiz.s2.noMaterialsYet": "Ei vielä tallennettuja tarvikkeita. Voit lisätä niitä",
    "wiz.s2.picks": "Pikavalinnat tässä raportissa",
    "wiz.s2.addCustomMaterial": "Raporttikohtainen tarvike…",
    "wiz.s3.title": "Tarkista ja luo",
    "wiz.s3.sub": "Voit muokata kaikkea myöhemminkin.",
    "wiz.s3.summary": "Yhteenveto",
    "wiz.s3.work": "Työ",
    "wiz.s3.propType": "Kohdetyyppi",
    "wiz.s3.units": "Yksiköt",
    "wiz.s3.unitsCount": "kpl",
    "wiz.s3.checklist": "Työvaiheet / checklist",
    "wiz.s3.materials": "Tarvikkeet – pikavalinnat",
    "wiz.s3.materialsNote": "Huom: Nämä ovat vain valittavissa olevia pikavalintoja. Käytetyt määrät kirjataan yksikkökohtaisesti kentällä.",
    "wiz.s3.customer": "Asiakas",
    "wiz.s3.address": "Osoite",
    "wiz.s3.executor": "Suorittaja",
    "wiz.toast.created": "Raportti luotu",
    "wiz.toast.createFailed": "Luonti epäonnistui",
    "wiz.toast.checklistAdded": "Lisätty",
    "wiz.toast.steps": "työvaihetta",

    /* quick report */
    "qr.back": "Takaisin",
    "qr.eyebrow": "Pikaraportti",
    "qr.titleNew": "Uusi pikaraportti",
    "qr.titleDefault": "Pikaraportti",
    "qr.subtitle": "Nopea raportti ilman vaiheita ja yksiköitä. Voit liittää sen kohteeseen myöhemmin.",
    "qr.preview": "Esikatselu",
    "qr.downloadPdf": "Lataa PDF",
    "qr.shareLink": "Jaa linkki",
    "qr.linkCopied": "Linkki kopioitu",
    "qr.copyManual": "Kopioi linkki manuaalisesti",
    "qr.delete": "Poista",
    "qr.deleteConfirm": "Poistetaanko pikaraportti?",
    "qr.deleted": "Poistettu",
    "qr.deleteFailed": "Poisto epäonnistui",
    "qr.workName": "Työn nimi",
    "qr.workNamePh": "esim. IV-konehuoneen pikatarkastus",
    "qr.notes": "Lisätiedot",
    "qr.findings": "Havainnot / jatkotoimenpiteet",
    "qr.findingsOptional": "(valinnainen)",
    "qr.findingsPh": "esim. 'Asiakas pyysi tarjousta IV-koneesta' tai 'IV-kanavassa epäilys vuodosta — vaatii lisätarkastuksen'",
    "qr.showToCustomer": "Näytä asiakkaalle",
    "qr.notifyOffice": "Ilmoita toimistolle",
    "qr.hiddenHint": "Tämä havainto piilotetaan asiakkaan PDF:stä ja jakolinkistä.",
    "qr.customer": "Asiakas",
    "qr.customerPh": "As Oy Kirjokuja",
    "qr.contactDetails": "Tarkemmat yhteystiedot",
    "qr.contactName": "Yhteyshenkilön nimi",
    "qr.contactNamePh": "esim. Matti Meikäläinen",
    "qr.phone": "Puhelin",
    "qr.email": "Sähköposti",
    "qr.address": "Osoite",
    "qr.photos": "Kuvat",
    "qr.captionPh": "Kirjoita kuvateksti — esim. mitä kuvassa näkyy, mistä kohteesta, mahdollinen vaurio…",
    "qr.removePhoto": "Poista kuva",
    "qr.takePhoto": "Ota kuva",
    "qr.fromGallery": "Galleriasta",
    "qr.cancel": "Peruuta",
    "qr.create": "Luo pikaraportti",
    "qr.saving": "Tallennetaan…",
    "qr.save": "Tallenna",
    "qr.titleRequired": "Anna työn nimi",
    "qr.savedToast": "Pikaraportti tallennettu",
    "qr.savedToastShort": "Tallennettu",
    "qr.saveFailed": "Tallennus epäonnistui",
    "qr.notFound": "Pikaraporttia ei löytynyt",
    "qr.materials": "Käytetyt tarvikkeet",
    "qr.materialPh": "Tarvike",
    "qr.materialQtyPh": "Määrä",
    "qr.row": "rivi",
    "qr.rows": "riviä",
    "qr.removeMaterial": "Poista tarvike",

    /* properties */
    "props.eyebrow": "Kohteet",
    "props.groupsTitle": "Pääryhmät",
    "props.groupsSub": "Selaa kohteesi pääryhmittäin.",
    "props.newGroup": "Uusi pääryhmä",
    "props.newGroupPh": "Pääryhmän nimi (esim. Taloyhtiöt)",
    "props.create": "Luo",
    "props.cancel": "Peruuta",
    "props.loading": "Ladataan…",
    "props.emptyGroups": "Ei vielä pääryhmiä",
    "props.emptyGroupsSub": "Luo ensimmäinen pääryhmä, esim. \"Taloyhtiöt\" tai \"Yksityisasiakkaat\".",
    "props.propertiesCount": "kohdetta",
    "props.deleteGroupTitle": "Poista pääryhmä",
    "props.deleteGroupConfirm": "Poistetaanko pääryhmä",
    "props.groupCreated": "Pääryhmä luotu",
    "props.createFailed": "Luonti epäonnistui",
    "props.deleted": "Poistettu",
    "props.deleteFailed": "Poisto epäonnistui",
    "props.backToGroups": "Takaisin pääryhmiin",
    "props.groupEyebrow": "Pääryhmä",
    "props.newProperty": "Uusi kohde",
    "props.newPropertyPh": "Kohteen nimi (esim. As Oy Kirjokuja 4)",
    "props.emptyProperties": "Ei vielä kohteita",
    "props.emptyPropertiesSub": "Lisää ensimmäinen kohde tähän pääryhmään.",
    "props.propertyCreated": "Kohde luotu",
    "props.deletePropertyConfirm": "Poistetaanko kohde",
    "props.deletePropertyTail": "Raportit säilyvät mutta menettävät kohteen.",
    "props.reportsCount": "raporttia",
    "props.propertyEyebrow": "Kohde",
    "props.createReportHere": "Luo raportti tähän kohteeseen",
    "props.emptyReports": "Ei vielä raportteja",
    "props.emptyReportsSub": "Luo ensimmäinen raportti tähän kohteeseen.",
    "props.createReport": "Luo raportti",
    "props.unitsCount": "yksikköä",
    "props.statDone": "Käsitelty",
    "props.statExtra": "Vaatii huomiota",
    "props.statBlocked": "Estynyt",

    /* common toasts */
    "toast.deleteReportConfirm": "Poistetaanko raportti?",
    "toast.deleteNoticeConfirm": "Poistetaanko tiedote?",
    "toast.reportDeleted": "Raportti poistettu",
    "toast.noticeDeleted": "Tiedote poistettu",
    "toast.deleteFailed": "Poisto epäonnistui",

    /* login */
    "login.title": "Tervetuloa takaisin",
    "login.subtitle": "Kirjaudu sisään jatkaaksesi raportointia.",
    "login.email": "Sähköposti",
    "login.password": "Salasana",
    "login.submit": "Kirjaudu sisään",
    "login.submitting": "Kirjaudutaan...",
    "login.google": "Jatka Googlella",
    "login.noAccount": "Eikö sinulla ole tiliä?",
    "login.signUp": "Luo tili ilmaiseksi",
    "login.forgotPassword": "Unohditko salasanasi?",
    "login.sidePromo": "30 päivää ilmaiseksi",
    "login.sidePromoTitle": "Selkeä raportointi työn aikana.",

    /* register */
    "register.title": "Aloita ilmaiseksi",
    "register.subtitle": "Luo tili, niin pääset raportoimaan heti.",
    "register.name": "Nimi",
    "register.companyName": "Yritys",
    "register.email": "Sähköposti",
    "register.password": "Salasana",
    "register.submit": "Luo tili",
    "register.submitting": "Luodaan...",
    "register.google": "Jatka Googlella",
    "register.hasAccount": "Onko sinulla jo tili?",
    "register.signIn": "Kirjaudu sisään",

    /* common */
    "common.save": "Tallenna",
    "common.cancel": "Peruuta",
    "common.delete": "Poista",
    "common.edit": "Muokkaa",
    "common.add": "Lisää",
    "common.back": "Takaisin",
    "common.next": "Seuraava",
    "common.done": "Valmis",
    "common.loading": "Ladataan…",
    "common.optional": "(valinnainen)",

    /* language */
    "lang.language": "Kieli",
    "lang.fi": "Suomi",
    "lang.en": "English",
  },
  en: {
    /* nav / appshell */
    "nav.menu": "Menu",
    "nav.home": "Home",
    "nav.newReport": "New report",
    "nav.newQuick": "New quick report",
    "nav.newNotice": "New resident notice",
    "nav.properties": "Properties",
    "nav.templates": "Templates",
    "nav.settings": "Settings",
    "nav.logout": "Log out",
    "nav.user": "User",
    "nav.asetukset": "Settings",
    "nav.kirjauduUlos": "Log out",

    /* dashboard */
    "dash.eyebrow": "Dashboard",
    "dash.title": "Welcome",
    "dash.subtitle": "Reports and resident notices in one place.",
    "dash.subtitleNoNotices": "All your reports in one place.",
    "dash.createNotice": "Create notice",
    "dash.createQuick": "Create quick report",
    "dash.createReport": "New report",
    "dash.searchPlaceholder": "Search by name, customer, address…",
    "dash.allStatuses": "All statuses",
    "dash.clear": "Clear",
    "dash.tabReports": "Reports",
    "dash.tabRecentReports": "Recent reports",
    "dash.tabQuick": "Quick reports",
    "dash.tabRecentQuick": "Recent quick reports",
    "dash.tabNotices": "Resident notices",
    "dash.tabRecentNotices": "Recent resident notices",
    "dash.loading": "Loading…",
    "dash.noMatches": "No matches",
    "dash.tryDifferent": "Try different search terms.",
    "dash.emptyReportsUnfiled": "No unfiled reports",
    "dash.emptyReports": "No reports yet",
    "dash.emptyReportsSub": "Create your first report in a few clicks.",
    "dash.emptyCreateReport": "Create report",
    "dash.emptyQuick": "No quick reports yet",
    "dash.emptyQuickSub": "A quick report is a fast way to log work with photos and text – no steps or units.",
    "dash.emptyNotices": "No notices yet",
    "dash.emptyNoticesSub": "Create a clean resident notice in minutes – pick a template, fill it in, print or share.",
    "dash.unitOne": "unit",
    "dash.unitMany": "units",
    "dash.photosCount": "photos",
    "dash.noPhotos": "No photos",
    "dash.toOffice": "To office",
    "dash.toOfficeTitle": "This report contains a finding flagged for the office",
    "dash.moveToProperty": "Move to property",
    "dash.deleteTitle": "Delete",
    "dash.crumbAll": "All",
    "dash.crumbUnfiled": "Unfiled",
    "dash.seeAll": "See all",
    "dash.reportsLower": "reports in properties",

    /* status badges */
    "status.draft": "Draft",
    "status.in_progress": "In progress",
    "status.completed": "Completed",
    "status.sent": "Sent",

    /* report wizard */
    "wiz.back": "Back",
    "wiz.eyebrow": "New report",
    "wiz.heading": "Create a report in a few steps.",
    "wiz.step": "Step",
    "wiz.steps.work": "Work",
    "wiz.steps.units": "Units",
    "wiz.steps.save": "Save",
    "wiz.steps.summary": "Summary",
    /* unit kinds */
    "wiz.kind.huoneistot": "Apartments",
    "wiz.kind.huoneistot.tpl": "Apartment",
    "wiz.kind.laitteet": "Equipment",
    "wiz.kind.laitteet.tpl": "Device",
    "wiz.kind.tarkastuspisteet": "Inspection points",
    "wiz.kind.tarkastuspisteet.tpl": "Point",
    "wiz.kind.tilat": "Spaces",
    "wiz.kind.tilat.tpl": "Space",
    "wiz.kind.ajoneuvot": "Vehicles",
    "wiz.kind.ajoneuvot.tpl": "Vehicle",
    "wiz.kind.rakennukset": "Buildings",
    "wiz.kind.rakennukset.tpl": "Building",
    "wiz.kind.mukautettu": "Custom / Work steps",
    "wiz.kind.default.tpl": "Item",
    "wiz.customPlaceholder": "A1\nA2\nA3\nBoiler room\nChimney 1\nChimney 2\nSauna section",

    /* property picker */
    "pp.label": "Property (optional)",
    "pp.none": "No property",
    "pp.loading": "Loading…",
    "pp.noGroupsYet": "No groups yet. Create the first one below.",
    "pp.noProps": "No properties",
    "pp.selected": "Selected",
    "pp.createNew": "Create new property",
    "pp.propNamePh": "Property name (e.g. Acme Building 4)",
    "pp.group": "Group",
    "pp.pickGroup": "Pick a group…",
    "pp.newGroup": "+ Create a new group",
    "pp.newGroupPh": "New group name (e.g. Housing companies)",
    "pp.cancel": "Cancel",
    "pp.creating": "Creating…",
    "pp.create": "Create property",
    "pp.nameRequired": "Enter a property name",
    "pp.groupNameRequired": "Enter a group name",
    "pp.createdToast": "Property created",
    "pp.createFailed": "Failed to create property",
    "pp.unfiledGroup": "Unfiled",
    "wiz.previous": "Previous",
    "wiz.next": "Next",
    "wiz.creating": "Creating…",
    "wiz.create": "Create report",
    "wiz.s0.title": "What work needs to be done?",
    "wiz.s0.sub": "Write the work name freely.",
    "wiz.s0.workName": "Work name",
    "wiz.s0.workPlaceholder": "e.g. Trap replacement, Filter swap, Electrical inspection, Roof maintenance…",
    "wiz.s0.propType": "Property type",
    "wiz.s0.propTypeOptional": "(optional)",
    "wiz.s0.propTypePlaceholder": "e.g. apartment building, hall, warehouse, office…",
    "wiz.s1.title": "What units or steps are covered in this report?",
    "wiz.s1.sub": "Pick the units, spaces, equipment or steps you cover in this report.",
    "wiz.s1.pickKind": "Pick unit type",
    "wiz.s1.labelPrefix": "Unit label (prefix)",
    "wiz.s1.count": "Count",
    "wiz.s1.customLabel": "Unit names (one per line)",
    "wiz.s1.customHint": "you can add more later on site.",
    "wiz.s1.preview": "Preview",
    "wiz.s1.moreLabel": "more",
    "wiz.s2.title": "Property and report content",
    "wiz.s2.sub": "Customer details can be filled later from the report view. You can leave other fields empty and come back to them in the field.",
    "wiz.s2.executor": "Executor company",
    "wiz.s2.executorPlaceholder": "Your company (shown on the report)",
    "wiz.s2.askExecutor": "Ask for work executor",
    "wiz.s2.askExecutorHint": "Uncheck if you don't want a separate executor field on the report.",
    "wiz.s2.property": "Property (optional)",
    "wiz.s2.checklist": "Checklist / steps",
    "wiz.s2.checklistHint": "Pick a standard company checklist or add report-specific steps. Steps are copied to every unit.",
    "wiz.s2.companyChecklists": "Company checklists",
    "wiz.s2.noChecklistsYet": "No saved checklists yet. You can add them in",
    "wiz.s2.inSettings": "Settings",
    "wiz.s2.addStep": "Add a report-specific step…",
    "wiz.s2.materials": "Materials – quick picks",
    "wiz.s2.materialsHint": "These are materials available in this report – not used materials. The field user adds the actually used materials per unit.",
    "wiz.s2.companyMaterials": "Company default materials",
    "wiz.s2.noMaterialsYet": "No saved materials yet. You can add them in",
    "wiz.s2.picks": "Quick picks in this report",
    "wiz.s2.addCustomMaterial": "Report-specific material…",
    "wiz.s3.title": "Review and create",
    "wiz.s3.sub": "You can edit anything later.",
    "wiz.s3.summary": "Summary",
    "wiz.s3.work": "Work",
    "wiz.s3.propType": "Property type",
    "wiz.s3.units": "Units",
    "wiz.s3.unitsCount": "pcs",
    "wiz.s3.checklist": "Steps / checklist",
    "wiz.s3.materials": "Materials – quick picks",
    "wiz.s3.materialsNote": "Note: these are only selectable quick picks. Used amounts are entered per unit in the field.",
    "wiz.s3.customer": "Customer",
    "wiz.s3.address": "Address",
    "wiz.s3.executor": "Executor",
    "wiz.toast.created": "Report created",
    "wiz.toast.createFailed": "Creation failed",
    "wiz.toast.checklistAdded": "Added",
    "wiz.toast.steps": "steps",

    /* quick report */
    "qr.back": "Back",
    "qr.eyebrow": "Quick report",
    "qr.titleNew": "New quick report",
    "qr.titleDefault": "Quick report",
    "qr.subtitle": "Fast report without steps or units. You can link it to a property later.",
    "qr.preview": "Preview",
    "qr.downloadPdf": "Download PDF",
    "qr.shareLink": "Share link",
    "qr.linkCopied": "Link copied",
    "qr.copyManual": "Copy the link manually",
    "qr.delete": "Delete",
    "qr.deleteConfirm": "Delete this quick report?",
    "qr.deleted": "Deleted",
    "qr.deleteFailed": "Delete failed",
    "qr.workName": "Work name",
    "qr.workNamePh": "e.g. AHU room quick check",
    "qr.notes": "Notes",
    "qr.findings": "Findings / next steps",
    "qr.findingsOptional": "(optional)",
    "qr.findingsPh": "e.g. 'Customer asked for a quote for the AHU' or 'Possible leak in the duct — needs further inspection'",
    "qr.showToCustomer": "Show to customer",
    "qr.notifyOffice": "Notify office",
    "qr.hiddenHint": "This finding is hidden from the customer PDF and share link.",
    "qr.customer": "Customer",
    "qr.customerPh": "Acme Ltd.",
    "qr.contactDetails": "Detailed contact info",
    "qr.contactName": "Contact name",
    "qr.contactNamePh": "e.g. John Smith",
    "qr.phone": "Phone",
    "qr.email": "Email",
    "qr.address": "Address",
    "qr.photos": "Photos",
    "qr.captionPh": "Write a caption — e.g. what's visible, which spot, any damage…",
    "qr.removePhoto": "Remove photo",
    "qr.takePhoto": "Take a photo",
    "qr.fromGallery": "From gallery",
    "qr.cancel": "Cancel",
    "qr.create": "Create quick report",
    "qr.saving": "Saving…",
    "qr.save": "Save",
    "qr.titleRequired": "Enter a work name",
    "qr.savedToast": "Quick report saved",
    "qr.savedToastShort": "Saved",
    "qr.saveFailed": "Save failed",
    "qr.notFound": "Quick report not found",
    "qr.materials": "Used materials",
    "qr.materialPh": "Material",
    "qr.materialQtyPh": "Qty",
    "qr.row": "row",
    "qr.rows": "rows",
    "qr.removeMaterial": "Remove material",

    /* properties */
    "props.eyebrow": "Properties",
    "props.groupsTitle": "Groups",
    "props.groupsSub": "Browse your properties by group.",
    "props.newGroup": "New group",
    "props.newGroupPh": "Group name (e.g. Housing companies)",
    "props.create": "Create",
    "props.cancel": "Cancel",
    "props.loading": "Loading…",
    "props.emptyGroups": "No groups yet",
    "props.emptyGroupsSub": "Create the first group, e.g. \"Housing companies\" or \"Private customers\".",
    "props.propertiesCount": "properties",
    "props.deleteGroupTitle": "Delete group",
    "props.deleteGroupConfirm": "Delete the group",
    "props.groupCreated": "Group created",
    "props.createFailed": "Creation failed",
    "props.deleted": "Deleted",
    "props.deleteFailed": "Delete failed",
    "props.backToGroups": "Back to main groups",
    "props.groupEyebrow": "Group",
    "props.newProperty": "New property",
    "props.newPropertyPh": "Property name (e.g. Acme Building 4)",
    "props.emptyProperties": "No properties yet",
    "props.emptyPropertiesSub": "Add the first property to this group.",
    "props.propertyCreated": "Property created",
    "props.deletePropertyConfirm": "Delete the property",
    "props.deletePropertyTail": "Reports stay but lose the property link.",
    "props.reportsCount": "reports",
    "props.propertyEyebrow": "Property",
    "props.createReportHere": "Create a report in this property",
    "props.emptyReports": "No reports yet",
    "props.emptyReportsSub": "Create the first report in this property.",
    "props.createReport": "Create report",
    "props.unitsCount": "units",
    "props.statDone": "Handled",
    "props.statExtra": "Needs attention",
    "props.statBlocked": "Blocked",

    /* common toasts */
    "toast.deleteReportConfirm": "Delete this report?",
    "toast.deleteNoticeConfirm": "Delete this notice?",
    "toast.reportDeleted": "Report deleted",
    "toast.noticeDeleted": "Notice deleted",
    "toast.deleteFailed": "Delete failed",

    /* login */
    "login.title": "Welcome back",
    "login.subtitle": "Sign in to continue reporting.",
    "login.email": "Email",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.submitting": "Signing in...",
    "login.google": "Continue with Google",
    "login.noAccount": "Don't have an account?",
    "login.signUp": "Create a free account",
    "login.forgotPassword": "Forgot your password?",
    "login.sidePromo": "30 days free",
    "login.sidePromoTitle": "Clear field reporting during the work.",

    /* register */
    "register.title": "Start free",
    "register.subtitle": "Create an account and start reporting in minutes.",
    "register.name": "Name",
    "register.companyName": "Company",
    "register.email": "Email",
    "register.password": "Password",
    "register.submit": "Create account",
    "register.submitting": "Creating...",
    "register.google": "Continue with Google",
    "register.hasAccount": "Already have an account?",
    "register.signIn": "Sign in",

    /* common */
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.add": "Add",
    "common.back": "Back",
    "common.next": "Next",
    "common.done": "Done",
    "common.loading": "Loading…",
    "common.optional": "(optional)",

    /* language */
    "lang.language": "Language",
    "lang.fi": "Suomi",
    "lang.en": "English",
  },
};

const LS_KEY = "veraso:lang";

export const getStoredLang = () => {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(LS_KEY);
    return v === "fi" || v === "en" ? v : null;
  } catch { return null; }
};

export const setStoredLang = (lang) => {
  if (lang !== "fi" && lang !== "en") return;
  try { window.localStorage.setItem(LS_KEY, lang); } catch { /* ignore */ }
};

/**
 * Hook to access the active translation function.
 *
 * Precedence (highest → lowest):
 *   1. Logged-in user's `language` field from /auth/me
 *   2. localStorage `veraso:lang` (set by landing-page toggle)
 *   3. Default "fi"
 */
export const useT = () => {
  const { user } = useAuth();
  const userLang = user?.language === "en" ? "en" : user?.language === "fi" ? "fi" : null;
  const stored = getStoredLang();
  const lang = userLang || stored || "fi";

  const dict = DICT[lang] || DICT.fi;
  const t = (key, fallback) => {
    if (dict[key] != null) return dict[key];
    if (DICT.fi[key] != null) return DICT.fi[key];
    if (DICT.en[key] != null) return DICT.en[key];
    return fallback != null ? fallback : key;
  };
  return { t, lang, isEn: lang === "en" };
};
